/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import { useCallback, useMemo } from 'react';
import { add, format } from 'date-fns';
import { IntlShape, useIntl } from 'react-intl';

import {
  AvailableColumnCalculations,
  FinancialConfig,
  FinancialDatasetRequest,
  ForecastComparisonDetails,
  ForecastComparisonRequest,
  ForecastListItem,
  LiveReportDetail,
  LiveReportList,
  ReportingTemplateFormula,
  RowFormula
} from '@futrli-sdk/predict';
import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';
import { formatCurrency } from '@futrli/localisation';
import { translateCalculation } from '@futrli/backend-messages/reporting/calculations';

import {
  actualForecastIds,
  datasetComparisonRef,
  DATE_FORMAT,
  dummyBudgetForecastId,
  dummyLiveForecastId,
  dummyScenarioForecastId,
  globalFinancialRefs,
  totalVarianceRef,
  v2ComparisonColumnRefs,
  WIDGET_VERSION
} from './constants';
import {
  CARD_TYPES,
  COMPARISON_OPTIONS,
  DataField,
  GROUPING_OPTIONS,
  LayoutData,
  PAGE_NUMBERS_DISPLAY,
  PERIOD_END_OPTIONS,
  PERIOD_OPTIONS,
  PeriodEndOption,
  ReportMappingData,
  ReportTemplate,
  ROUNDING_OPTIONS,
  SCALING_OPTIONS,
  STROKE_BORDER_POSITIONS,
  TABLE_FONT_WEIGHTS,
  VALUE_DISPLAY_OPTIONS,
  VALUE_TREATMENT
} from './types';
import { forceLocalDate } from '../utils/dates';
import { getDataReferencesFromReport, removeDeletedFinFields } from './utils-data';
import {
  getForecastComparisonDatasets,
  getForecastComparisons,
  getForecastComparisonVariances,
  getWidgetOptions,
  getWidgetQueryFieldRefs,
  getWidgetSelectedVariances,
  isActualsOnlyDataset,
  isWidgetFieldUsingAssumptionsGrouping,
  isWidgetReadyForComparisonQuerying,
  isWidgetReadyForQuerying
} from './utils-queries';
import { getPeriodOffset, getPeriodType } from './utils-dates';

const dummyForecastPermissionInfo = {
  owner_id: null,
  sharing_status: 'private' as const,
  effective_permission: 'edit' as const
};

export const useI18nConstants = () => {
  const intl = useIntl();

  const liveForecastName = intl.formatMessage({
    id: 'reporting.daily_tracker_forecast',
    defaultMessage: 'Daily tracker forecast'
  });

  const groupingOptions = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: 'reporting.grouping.categories', defaultMessage: 'Categories' }),
        value: GROUPING_OPTIONS.CATEGORIES
      },
      {
        name: intl.formatMessage({ id: 'reporting.grouping.groups', defaultMessage: 'Groups' }),
        value: GROUPING_OPTIONS.GROUPS
      },
      {
        name: intl.formatMessage({ id: 'reporting.grouping.accounts', defaultMessage: 'Accounts' }),
        value: GROUPING_OPTIONS.ACCOUNTS
      },
      {
        name: intl.formatMessage({ id: 'reporting.grouping.predictions', defaultMessage: 'Predictions' }),
        value: GROUPING_OPTIONS.ASSUMPTIONS
      }
    ],
    [intl]
  );

  const driversGroupingOptions = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: 'reporting.grouping.groups', defaultMessage: 'Groups' }),
        value: GROUPING_OPTIONS.DRIVER_GROUPS
      },
      {
        name: intl.formatMessage({ id: 'reporting.grouping.drivers', defaultMessage: 'Drivers' }),
        value: GROUPING_OPTIONS.DRIVERS
      }
    ],
    [intl]
  );

  const datasetComparisonChildren = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: 'reporting.financial_variance', defaultMessage: 'Financial variance' }),
        ref: 'v1:accrual:dataset_comparison:variance_value',
        classifier: '',
        children: []
      },
      {
        name: intl.formatMessage({ id: 'reporting.percentage_variance', defaultMessage: '% variance' }),
        ref: 'v1:accrual:dataset_comparison:variance_percent',
        classifier: '',
        children: []
      },
      {
        name: intl.formatMessage({ id: 'reporting.total', defaultMessage: 'Total' }),
        ref: totalVarianceRef,
        classifier: '',
        children: [],
        isGeneric: true
      }
    ],
    [intl]
  );

  const v2DatasetComparisonChildren = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: 'reporting.financial_variance', defaultMessage: 'Financial variance' }),
        ref: v2ComparisonColumnRefs.VARIANCE_VALUE,
        classifier: '',
        children: []
      },
      {
        name: intl.formatMessage({ id: 'reporting.percentage_variance', defaultMessage: '% variance' }),
        ref: v2ComparisonColumnRefs.VARIANCE_PERCENT,
        classifier: '',
        children: []
      }
    ],
    [intl]
  );

  // TODO: replace these dummy forecasts (and their budget / scenario / live kinds) with the available datasources
  // schema (organisation-actuals / organisation-forecast), which has no notion of budget or scenario.
  const dummyTemplateForecasts: ForecastListItem[] = useMemo(
    () => [
      {
        id: dummyLiveForecastId,
        name: liveForecastName,
        kind: 'live',
        status: 'published',
        is_frozen: true,
        created: '',
        modified: '',
        forecast_from_date: '',
        window_from: '',
        window_till: '',
        window_spec: {
          kind: 'custom-years',
          duration_years: 0,
          actuals_financial_years: 0,
          forecast_financial_years: 0
        },
        owner_id: '',
        permission_info: dummyForecastPermissionInfo,
        include_unpaid_invoices: false,
        is_hidden: false,
        include_ai_assumptions: false,
        checklist: []
      },
      {
        id: dummyBudgetForecastId,
        name: intl.formatMessage({ id: 'reporting.forecast_1', defaultMessage: 'Forecast 1' }),
        kind: 'budget',
        status: 'published',
        is_frozen: true,
        created: '',
        modified: '',
        forecast_from_date: '',
        window_from: '',
        window_till: '',
        window_spec: {
          kind: 'custom-years',
          duration_years: 0,
          actuals_financial_years: 0,
          forecast_financial_years: 0
        },
        owner_id: '',
        permission_info: dummyForecastPermissionInfo,
        include_unpaid_invoices: false,
        is_hidden: false,
        include_ai_assumptions: false,
        checklist: []
      },
      {
        id: dummyScenarioForecastId,
        name: intl.formatMessage({ id: 'reporting.forecast_2', defaultMessage: 'Forecast 2' }),
        kind: 'scenario',
        status: 'published',
        is_frozen: true,
        created: '',
        modified: '',
        forecast_from_date: '',
        window_from: '',
        window_till: '',
        window_spec: {
          kind: 'custom-years',
          duration_years: 0,
          actuals_financial_years: 0,
          forecast_financial_years: 0
        },
        owner_id: '',
        permission_info: dummyForecastPermissionInfo,
        include_unpaid_invoices: false,
        is_hidden: false,
        include_ai_assumptions: false,
        checklist: []
      }
    ],
    [intl, liveForecastName]
  );

  const actualForecasts: ForecastListItem[] = useMemo(
    () =>
      [
        {
          id: actualForecastIds[0],
          name: intl.formatMessage({ id: 'reporting.actual_data', defaultMessage: 'Actual data' }),
          created: '',
          modified: '',
          kind: 'live',
          status: 'published',
          is_frozen: false,
          forecast_from_date: format(add(new Date(), { days: -1 }), DATE_FORMAT),
          window_from: '',
          window_till: '',
          window_spec: {
            kind: 'custom-years',
            duration_years: 0,
            actuals_financial_years: 0,
            forecast_financial_years: 0
          },
          owner_id: '',
          permission_info: dummyForecastPermissionInfo,
          include_unpaid_invoices: false,
          is_hidden: false,
          include_ai_assumptions: false,
          checklist: []
        },
        {
          id: actualForecastIds[1],
          name: intl.formatMessage({ id: 'reporting.prior_year_actuals', defaultMessage: 'Prior year actuals' }),
          created: '',
          modified: '',
          kind: 'live',
          status: 'published',
          is_frozen: false,
          forecast_from_date: format(add(new Date(), { years: 1, days: -1 }), DATE_FORMAT),
          window_from: '',
          window_till: '',
          window_spec: {
            kind: 'custom-years',
            duration_years: 0,
            actuals_financial_years: 0,
            forecast_financial_years: 0
          },
          owner_id: '',
          permission_info: dummyForecastPermissionInfo,
          include_unpaid_invoices: false,
          is_hidden: false,
          include_ai_assumptions: false,
          checklist: []
        }
      ] as ForecastListItem[],
    [intl]
  );

  /**
   * get column formulas form calculation, grouped by specific list
   */
  const getTransformedColumnFormulas = useCallback(
    (calculations?: AvailableColumnCalculations) => {
      if (!calculations) {
        return [];
      }

      const parsedData = [
        {
          name: intl.formatMessage({ id: 'reporting.period_columns', defaultMessage: 'Period columns' }),
          ref: 'period_columns',
          classifier: '',
          children: calculations.period_columns.map(d => ({
            name: translateCalculation(d.ref, intl) || d.name,
            ref: d.ref,
            classifier: '',
            children: []
          }))
        },
        {
          name: intl.formatMessage({ id: 'reporting.summary_columns', defaultMessage: 'Summary columns' }),
          ref: 'summary_columns',
          classifier: '',
          children: calculations.summary_columns.map(d => ({
            name: translateCalculation(d.ref, intl) || d.name,
            ref: d.ref,
            classifier: '',
            children: []
          }))
        },
        {
          name: intl.formatMessage({ id: 'reporting.comparison_columns', defaultMessage: 'Comparison columns' }),
          ref: 'comparison-columns',
          classifier: '',
          children: [
            {
              name: intl.formatMessage({ id: 'common.monthly', defaultMessage: 'Monthly' }),
              ref: 'monthly',
              classifier: '',
              disabledIfMultiDS: true,
              children: calculations.monthly.map(d => ({
                name: translateCalculation(d.ref, intl) || d.name,
                ref: d.ref,
                classifier: '',
                disabledIfMultiDS: true,
                children: []
              }))
            },
            {
              name: intl.formatMessage({ id: 'common.quarterly', defaultMessage: 'Quarterly' }),
              ref: 'quarterly',
              classifier: '',
              disabledIfMultiDS: true,
              children: calculations.quarterly.map(d => ({
                name: translateCalculation(d.ref, intl) || d.name,
                ref: d.ref,
                classifier: '',
                disabledIfMultiDS: true,
                children: []
              }))
            },
            {
              name: 'Financial yearly',
              ref: 'financial_yearly',
              classifier: '',
              disabledIfMultiDS: true,
              children: calculations.financial_yearly.map(d => ({
                name: translateCalculation(d.ref, intl) || d.name,
                ref: d.ref,
                classifier: '',
                disabledIfMultiDS: true,
                children: []
              }))
            },
            {
              name: intl.formatMessage({
                id: 'reporting.dataset_comparison',
                defaultMessage: 'Dataset comparison'
              }),
              ref: datasetComparisonRef,
              classifier: '',
              disabledIfOneDS: true,
              children: v2DatasetComparisonChildren.map(d => ({
                ...d,
                name: translateCalculation(d.ref, intl) || d.name,
                disabledIfOneDS: true,
                children: []
              }))
            }
          ]
        }
      ];

      return parsedData as DataField[];
    },
    [intl, v2DatasetComparisonChildren]
  );

  /**
   * construct a query to get fin data from layout data of a widget
   */
  const getFinDataQueryForWidget = useCallback(
    (
      inputWidget: LayoutData,
      allFinFields: DataField[],
      allFormulas: (RowFormula | ReportingTemplateFormula)[],
      forecasts: (ForecastListItem | ConsolidatedForecastListItem)[],
      hasMultiForecast: boolean,
      isTemplate: boolean,
      financialConfig?: FinancialConfig,
      isConsolidation?: boolean
    ): FinancialDatasetRequest[] => {
      const isWidgetReadyForQuery = isWidgetReadyForQuerying(inputWidget);
      const queries: FinancialDatasetRequest[] = [];

      if (isWidgetReadyForQuery && financialConfig) {
        const widget = removeDeletedFinFields([inputWidget], allFinFields, allFormulas, undefined)[0];
        const widgetOptions = widget[CARD_TYPES.WIDGET]!;
        const { periodCount, startDate, startDate1YearPrevious } = getWidgetOptions(widget, financialConfig);
        const fields = getWidgetQueryFieldRefs(widget, allFinFields, allFormulas, true);
        const includeAssumptions = isWidgetFieldUsingAssumptionsGrouping(widget);
        if (fields.length) {
          // handling multi forecast scenarios
          if (widget.version === WIDGET_VERSION && hasMultiForecast) {
            (widgetOptions.selectedDatasets || [])
              .filter(ds => {
                const dataset = isTemplate
                  ? dummyTemplateForecasts.find(f => f.id === ds)
                  : forecasts.find(f => f.id === ds);
                const actuals_only = isActualsOnlyDataset(ds);
                return !!dataset || actuals_only;
              })
              .forEach(ds => {
                const actuals_only = isActualsOnlyDataset(ds);
                const queryStartDate = ds === 'prior-year-actuals' ? startDate1YearPrevious : startDate;

                queries.push(({
                  forecast_id: ds,
                  consolidated_forecast_id: ds,
                  fields: fields.map(ref => ({ ref })),
                  period_type: getPeriodType(widgetOptions.calendarOptions?.type) || '',
                  period_count: periodCount,
                  start_date: queryStartDate,
                  period_offset: getPeriodOffset(widgetOptions.calendarOptions?.type),
                  selected_variances: getWidgetSelectedVariances(widget, datasetComparisonChildren, false),
                  actuals_only,
                  include_assumptions: isConsolidation ? false : includeAssumptions
                } as unknown) as FinancialDatasetRequest);
              });
          }
        }
      }

      return queries;
    },
    [datasetComparisonChildren, dummyTemplateForecasts]
  );

  /**
   * construct a query to get fin comparison data from layout data of a widget
   */
  const getComparisonQueryForWidget = useCallback(
    (
      inputWidget: LayoutData,
      allFinFields: DataField[],
      allFormulas: (RowFormula | ReportingTemplateFormula)[],
      forecasts: (ForecastListItem | ConsolidatedForecastListItem)[],
      isTemplate: boolean,
      financialConfig?: FinancialConfig
    ): ForecastComparisonRequest | undefined => {
      const isWidgetReadyForQuery = isWidgetReadyForComparisonQuerying(inputWidget);

      if (isWidgetReadyForQuery && financialConfig) {
        const widget = removeDeletedFinFields([inputWidget], allFinFields, allFormulas, undefined)[0];
        const widgetOptions = widget[CARD_TYPES.WIDGET]!;
        const { periodCount } = getWidgetOptions(widget, financialConfig);

        const fields = getWidgetQueryFieldRefs(widget, allFinFields, allFormulas, false);
        const selectedVariances = getForecastComparisonVariances(widget);

        // handling multi forecast scenarios
        const { datasets, dsIdtoUUIDMap, dsUUIDToIdMap } = getForecastComparisonDatasets(
          widget,
          isTemplate ? dummyTemplateForecasts : forecasts,
          financialConfig
        );

        if (Object.keys(datasets).length > 1 && fields.length && selectedVariances.length) {
          const comparisons = getForecastComparisons(widget, forecasts, dsIdtoUUIDMap, dsUUIDToIdMap);

          return {
            datasets,
            period_type: getPeriodType(widgetOptions.calendarOptions?.type) || '',
            period_offset: getPeriodOffset(widgetOptions.calendarOptions?.type),
            period_count: periodCount!,
            fields: (fields.map(ref => ({ ref })) as unknown) as string[],
            comparisons: comparisons as ForecastComparisonDetails[],
            selected_variances: selectedVariances
          };
        }
      }
      return;
    },
    [dummyTemplateForecasts]
  );

  /**
   * checks if list of datasets has a template DS
   */
  const hasTemplateDS = useCallback(
    (datasets: string[] | undefined) => {
      if (!datasets || !datasets.length) {
        return false;
      }
      return !datasets.every(ds => {
        return !dummyTemplateForecasts.find(d => d.id === ds);
      });
    },
    [dummyTemplateForecasts]
  );

  /**
   * returns list of dummy template datasets used in a report
   */
  const getDummyTemplateDSInReport = useCallback(
    (reportData: LayoutData[]) => {
      if (!reportData || !reportData.length) {
        return [];
      }
      const templateDS: string[] = [];
      reportData.forEach(item => {
        if (item?.[CARD_TYPES.WIDGET]) {
          item?.[CARD_TYPES.WIDGET]?.selectedDatasets?.forEach(ds => {
            if (!templateDS.includes(ds) && dummyTemplateForecasts.find(d => d.id === ds)) {
              templateDS.push(ds);
            }
          });
        }
      });
      return templateDS;
    },
    [dummyTemplateForecasts]
  );

  /**
   * Returns Dataset references and line data references used in a report
   */
  const getDataReferencesFromReportCallback = useCallback(
    (report: LiveReportDetail) => {
      return getDataReferencesFromReport(
        report,
        actualForecasts.map(f => f.id),
        globalFinancialRefs
      );
    },
    [actualForecasts]
  );

  /**
   * Gets list of used datasets in report from futrli template, returns an object with:
   * - mappedDS: list of datasets to map to, if not found, it returns original dataset
   * - originalDS: list of original datasets
   * - DSToMap: list of datasets that were not able to map automatically so it needs a modal so user can choose what to map them to
   */
  const mapTemplateDatasets = useCallback(
    (datasets: string[] | undefined) => {
      if (!datasets) {
        return { mappedDS: [], originalDS: [], DSToMap: [] };
      }

      const DSToMap: string[] = [];
      const originalDS: string[] = [];
      const mappedDS = datasets.map(ds => {
        originalDS.push(ds);
        const af = actualForecasts.find(d => d.id === ds);
        if (!af) {
          const phf = dummyTemplateForecasts.find(d => d.id === ds);
          if (phf?.kind === 'live') {
            // the template's live dataset maps to the report's actual data
            return actualForecastIds[0];
          }
          // will be mapped in modal
          DSToMap.push(ds);
        }
        return ds;
      });
      return { mappedDS, originalDS, DSToMap };
    },
    [actualForecasts, dummyTemplateForecasts]
  );

  /**
   * Gets list of used datasets in report from workspace template, returns an object with:
   * - mappedDS: list of datasets to map to, if not found, it returns original dataset
   * - originalDS: list of original datasets
   * - DSToMap: list of datasets that were not able to map automatically so it needs a modal so user can choose what to map them to
   */
  const mapWorkspaceTemplateDatasets = useCallback(
    (
      datasets: string[] | undefined,
      mappingData: ReportMappingData | undefined,
      forecasts: (ForecastListItem | ConsolidatedForecastListItem)[]
    ) => {
      if (!datasets) {
        return { mappedDS: [], originalDS: [], DSToMap: [] };
      }

      // forecasts come from the available datasources endpoint, where the live forecast is the
      // organisation-actuals datasource, which is never part of this list
      const nonLiveForecasts = forecasts;
      const firstNonLiveForecast = nonLiveForecasts[0];

      const datasetsMappingArr = mappingData
        ? Object.keys(mappingData?.dataset_references || {}).map(key => mappingData.dataset_references[key])
        : [];
      const sourceNonLiveDatasetCount = datasetsMappingArr.filter(dataset => dataset.data.kind !== 'live').length;

      const DSToMap: string[] = [];
      const originalDS: string[] = [];
      const mappedDS = datasets.map(ds => {
        originalDS.push(ds);
        const af = actualForecasts.find(d => d.id === ds);
        if (af) {
          return ds;
        } else {
          const srcDS = datasetsMappingArr.find(d => d.id === ds);
          if (srcDS?.data.kind !== 'live' && (nonLiveForecasts.length !== 1 || sourceNonLiveDatasetCount !== 1)) {
            // will be mapped in modal
            DSToMap.push(ds);
            return ds;
          }
          if (srcDS?.data.kind === 'live') {
            return actualForecastIds[0];
          }
          if (
            srcDS?.data.kind !== 'live' &&
            nonLiveForecasts.length === 1 &&
            sourceNonLiveDatasetCount === 1 &&
            firstNonLiveForecast
          ) {
            return firstNonLiveForecast.id;
          }
        }
        return ds;
      });
      return { mappedDS, originalDS, DSToMap };
    },
    [actualForecasts]
  );

  /**
   * Generate report page number footer string based on selected options
   */
  const getPageNumberLabel = useCallback(
    (page: number, totalPages: number, pageNumbersDisplay: PAGE_NUMBERS_DISPLAY) => {
      switch (pageNumbersDisplay) {
        case PAGE_NUMBERS_DISPLAY.ONE:
          return page;
        case PAGE_NUMBERS_DISPLAY.ONE_OF_ONE:
          return intl.formatMessage(
            {
              id: 'reporting.number_value_of_total',
              defaultMessage: '{page} of {totalPages}'
            },
            { page, totalPages }
          );
        case PAGE_NUMBERS_DISPLAY.PAGE_ONE:
          return intl.formatMessage(
            {
              id: 'reporting.page_number_value',
              defaultMessage: 'Page {page}'
            },
            { page }
          );
        case PAGE_NUMBERS_DISPLAY.PAGE_ONE_OF_ONE:
          return intl.formatMessage(
            {
              id: 'reporting.page_number_value_of_total',
              defaultMessage: 'Page {page} of {totalPages}'
            },
            { page, totalPages }
          );
        default:
          return '';
      }
    },
    [intl]
  );

  /**
   * Get highest untitled report number, used when generating a title for a new report
   */
  const getHighestUntitledReportNum = useCallback(
    (
      reports: (LiveReportList | ReportTemplate)[],
      orientation: 'portrait' | 'landscape' | 'dashboard',
      name?: string
    ) => {
      const untitledReportLN = intl.formatMessage({
        id: 'reporting.untitled_report',
        defaultMessage: 'Untitled report'
      });
      const untitledDashboardLN = intl.formatMessage({
        id: 'reporting.untitled_dashboard',
        defaultMessage: 'Untitled dashboard'
      });
      const untitledReports = name
        ? reports.filter(r => r.name.toLocaleLowerCase().startsWith(name))
        : orientation === 'dashboard'
        ? reports.filter(r => r.name.toLocaleLowerCase().startsWith(untitledDashboardLN.toLocaleLowerCase()))
        : reports.filter(r => r.name.toLocaleLowerCase().startsWith(untitledReportLN.toLocaleLowerCase()));
      let highestURNbr = 0;
      untitledReports.forEach(r => {
        const n = +r.name
          .toLocaleLowerCase()
          .replace(
            name
              ? name
              : orientation === 'dashboard'
              ? untitledDashboardLN.toLocaleLowerCase()
              : untitledReportLN.toLocaleLowerCase(),
            ''
          )
          .trim();
        highestURNbr = n > highestURNbr ? n : highestURNbr;
      });

      return highestURNbr;
    },
    [intl]
  );

  /**
   * Get highest untitled template number, used when generating a title for a new template
   */
  const getHighestUntitledTemplateNum = useCallback(
    (templates: (LiveReportList | ReportTemplate)[]) => {
      const untitledTemplateLN = intl.formatMessage({
        id: 'reporting.untitled_template',
        defaultMessage: 'Untitled template'
      });
      const untitledTemplates = templates.filter(r =>
        r.name.toLocaleLowerCase().includes(untitledTemplateLN.toLocaleLowerCase())
      );
      let highestURNbr = 0;
      untitledTemplates.forEach(r => {
        const n = +r.name.toLocaleLowerCase().replace(untitledTemplateLN.toLocaleLowerCase(), '').trim();
        highestURNbr = n > highestURNbr ? n : highestURNbr;
      });

      return highestURNbr;
    },
    [intl]
  );

  const getDateFieldTitleDesc = useCallback(
    (
      comparisonPeriod: COMPARISON_OPTIONS,
      periodsNumber: number,
      startDateStr: string,
      fieldRef: string,
      indicator: PERIOD_OPTIONS,
      selectedYear: number,
      selectedQuarter: string
    ) => {
      const calculatedPeriodNumber =
        periodsNumber === 0
          ? ''
          : comparisonPeriod === COMPARISON_OPTIONS.PREVIOUS
          ? `(-${periodsNumber})`
          : `(+${periodsNumber})`;

      if (fieldRef === 'daily') {
        let startDate = '';
        if (startDateStr === PERIOD_OPTIONS.THIS_PERIOD) {
          startDate = 'today';
        } else if (startDateStr === PERIOD_OPTIONS.LAST_PERIOD) {
          startDate = 'yesterday';
        } else {
          startDate = format(new Date(startDateStr), 'd LLL yy');
        }
        return periodsNumber === 0 ? startDate : `${startDate} ${calculatedPeriodNumber}`;
      } else if (fieldRef === 'monthly') {
        if (indicator === PERIOD_OPTIONS.THIS_PERIOD) {
          return intl.formatMessage(
            { id: 'reporting.this_month_quantity', defaultMessage: 'This Month {quantity}' },
            { quantity: calculatedPeriodNumber }
          );
        } else if (indicator === PERIOD_OPTIONS.LAST_PERIOD) {
          return intl.formatMessage(
            { id: 'reporting.last_month_quantity', defaultMessage: 'Last Month {quantity}' },
            { quantity: calculatedPeriodNumber }
          );
        } else {
          const startDate = format(new Date(startDateStr), 'LLL yy');
          return `${startDate} ${calculatedPeriodNumber}`;
        }
      } else if (fieldRef === 'weekly_sun' || fieldRef === 'weekly_mon') {
        if (indicator === PERIOD_OPTIONS.THIS_PERIOD) {
          return intl.formatMessage(
            { id: 'reporting.this_week_quantity', defaultMessage: 'This week {quantity}' },
            { quantity: calculatedPeriodNumber }
          );
        } else if (indicator === PERIOD_OPTIONS.LAST_PERIOD) {
          return intl.formatMessage(
            { id: 'reporting.last_week_quantity', defaultMessage: 'Last week {quantity}' },
            { quantity: calculatedPeriodNumber }
          );
        } else {
          const startDate = format(new Date(startDateStr), 'd LLL yy');
          return `${
            fieldRef === 'weekly_mon'
              ? intl.formatMessage({ id: 'common.monday_abbr', defaultMessage: 'Mon' })
              : intl.formatMessage({ id: 'common.sunday_abbr', defaultMessage: 'Sun' })
          } ${startDate} ${calculatedPeriodNumber}`;
        }
      } else if (fieldRef === 'yearly') {
        if (indicator === PERIOD_OPTIONS.THIS_PERIOD) {
          return intl.formatMessage(
            { id: 'reporting.this_year_quantity', defaultMessage: 'This year {quantity}' },
            { quantity: calculatedPeriodNumber }
          );
        } else if (indicator === PERIOD_OPTIONS.LAST_PERIOD) {
          return intl.formatMessage(
            { id: 'reporting.last_year_quantity', defaultMessage: 'Last year {quantity}' },
            { quantity: calculatedPeriodNumber }
          );
        } else if (indicator === PERIOD_OPTIONS.OTHER) {
          return `${selectedYear} ${calculatedPeriodNumber}`;
        }
      } else {
        if (indicator === PERIOD_OPTIONS.THIS_PERIOD) {
          return intl.formatMessage(
            { id: 'reporting.this_quarter_quantity', defaultMessage: 'This quarter {quantity}' },
            { quantity: calculatedPeriodNumber }
          );
        } else if (indicator === PERIOD_OPTIONS.LAST_PERIOD) {
          return intl.formatMessage(
            { id: 'reporting.last_quarter_quantity', defaultMessage: 'Last quarter {quantity}' },
            { quantity: calculatedPeriodNumber }
          );
        } else if (indicator === PERIOD_OPTIONS.OTHER) {
          const startQuarterDate = forceLocalDate(selectedQuarter);
          const startQuarterMonth = format(startQuarterDate, 'LLL');
          const endQuarterDate = add(add(startQuarterDate, { months: 3 }), { days: -1 });
          const endQuarterMonth = format(endQuarterDate, 'LLL');
          const endQuarterYear = format(endQuarterDate, 'yy');
          return `${startQuarterMonth}-${endQuarterMonth} ${endQuarterYear} ${calculatedPeriodNumber}`;
        }
      }
    },
    [intl]
  );

  /**
   * format data for display in reporting
   */
  const roundAndFormatValue = useCallback(
    (
      v: number | string,
      rounding: ROUNDING_OPTIONS,
      type: VALUE_DISPLAY_OPTIONS,
      intl: IntlShape,
      currency: string,
      isTable = false,
      omitDecimalsForLargeNums = false
    ) => {
      if (v === 'nan' && isTable) {
        return '-';
      }
      if (isNaN(Number.parseFloat(`${v}`))) {
        return '0';
      }
      let letter = '',
        r = 0,
        m = 0,
        M = 0;
      let n = Number.parseFloat(`${v}`);
      if (type === VALUE_DISPLAY_OPTIONS.PERCENTAGE) {
        n *= 100;
      }
      if (rounding === ROUNDING_OPTIONS.MILLIONS) {
        r = Math.round(n / 10000) / 100;
        letter = intl.formatMessage({
          id: 'abbreviation.millions',
          description: 'Abbreviation for Millions',
          defaultMessage: 'M'
        });
        M = 2;
      }
      if (rounding === ROUNDING_OPTIONS.THOUSANDS) {
        r = Math.round(n / 10) / 100;
        letter = intl.formatMessage({
          id: 'abbreviation.thousands',
          description: 'Abbreviation for Thousands',
          defaultMessage: 'K'
        });
        M = 2;
      }
      if (rounding === ROUNDING_OPTIONS.INTEGER) {
        r = Math.round(n);
        M = 0;
      }
      if (rounding === ROUNDING_OPTIONS.ONE_DECIMAL) {
        r = Math.round(n * 10) / 10;
        m = 1;
        M = 1;
      }
      if (rounding === ROUNDING_OPTIONS.TWO_DECIMALS) {
        r = Math.round(n * 100) / 100;
        m = 2;
        M = 2;
      }
      if (type === VALUE_DISPLAY_OPTIONS.CURRENCY) {
        return `${formatCurrency(r, intl, currency, m, r > 1000 && omitDecimalsForLargeNums ? m : M)}${letter}`;
      } else if (type === VALUE_DISPLAY_OPTIONS.NUMBER) {
        return `${formatCurrency(r, intl, '', m, r > 1000 && omitDecimalsForLargeNums ? m : M)}${letter}`;
      } else {
        return `${formatCurrency(r, intl, '', m, r > 1000 && omitDecimalsForLargeNums ? m : M)}${letter}%`;
      }
    },
    []
  );

  const constants = useMemo(
    () => ({
      defaultEditableTitle: intl.formatMessage({
        id: 'reporting.click_here_to_edit_title',
        defaultMessage: 'Click here to edit your title'
      }),
      defaultEditableSubTitle: intl.formatMessage({
        id: 'reporting.click_here_to_edit_sub_title',
        defaultMessage: 'Click here to edit sub-title'
      }),
      liveForecastName,
      templateTags: [
        {
          name: intl.formatMessage({ id: 'reporting.executive_summaries', defaultMessage: 'Executive summaries' }),
          value: 'executive-summaries'
        },
        {
          name: intl.formatMessage({ id: 'financial_fields.type.profit_and_loss', defaultMessage: 'Profit & Loss' }),
          value: 'profit-and-loss'
        },
        {
          name: intl.formatMessage({ id: 'financial_fields.type.balance_sheet', defaultMessage: 'Balance Sheet' }),
          value: 'balance-sheet'
        },
        {
          name: intl.formatMessage({ id: 'financial_fields.type.cash_flow', defaultMessage: 'Cash Flow' }),
          value: 'cash-flow'
        },
        {
          name: intl.formatMessage({ id: 'reporting.1_page_templates', defaultMessage: '1-page templates' }),
          value: '1-page-templates'
        },
        {
          name: intl.formatMessage({ id: 'reporting.scenario_based', defaultMessage: 'Scenario based' }),
          value: 'scenario-based'
        },
        {
          name: intl.formatMessage({ id: 'reporting.budget_based', defaultMessage: 'Budget based' }),
          value: 'budget-based'
        },
        {
          name: intl.formatMessage({ id: 'reporting.daily_tracker_based', defaultMessage: 'Daily Tracker based' }),
          value: 'daily-tracker-based'
        },
        {
          name: intl.formatMessage({ id: 'reporting.historical', defaultMessage: 'Historical' }),
          value: 'historical'
        },
        {
          name: intl.formatMessage({ id: 'reporting.performance', defaultMessage: 'Performance' }),
          value: 'performance'
        },
        { name: intl.formatMessage({ id: 'reporting.titles', defaultMessage: 'Titles' }), value: 'titles' }
      ],
      dummyTemplateForecasts,
      datasetComparisonChildren,
      actualForecasts,
      comparisonOptions: [
        {
          name: intl.formatMessage({ id: 'common.the_prior', defaultMessage: 'the prior' }),
          value: COMPARISON_OPTIONS.PREVIOUS
        },
        {
          name: intl.formatMessage({ id: 'common.the_next', defaultMessage: 'the next' }),
          value: COMPARISON_OPTIONS.FUTURE
        }
      ],
      fontWeightOptions: [
        {
          name: intl.formatMessage({ id: 'reporting.font_weight.lighter', defaultMessage: 'Lighter' }),
          value: TABLE_FONT_WEIGHTS.LIGHTER
        },
        {
          name: intl.formatMessage({ id: 'common.normal', defaultMessage: 'Normal' }),
          value: TABLE_FONT_WEIGHTS.NORMAL
        },
        {
          name: intl.formatMessage({ id: 'reporting.font_weight.bold', defaultMessage: 'Bold' }),
          value: TABLE_FONT_WEIGHTS.BOLD
        },
        {
          name: intl.formatMessage({ id: 'reporting.font_weight.bolder', defaultMessage: 'Bolder' }),
          value: TABLE_FONT_WEIGHTS.BOLDER
        }
      ],
      borderPositionOptions: [
        {
          name: intl.formatMessage({ id: 'common.border_none', defaultMessage: 'None' }),
          value: STROKE_BORDER_POSITIONS.NONE
        },
        {
          name: intl.formatMessage({ id: 'reporting.border.top_border', defaultMessage: 'Top border' }),
          value: STROKE_BORDER_POSITIONS.TOP
        },
        {
          name: intl.formatMessage({ id: 'reporting.border.right_border', defaultMessage: 'Right border' }),
          value: STROKE_BORDER_POSITIONS.RIGHT
        },
        {
          name: intl.formatMessage({ id: 'reporting.border.bottom_border', defaultMessage: 'Bottom border' }),
          value: STROKE_BORDER_POSITIONS.BOTTOM
        },
        {
          name: intl.formatMessage({ id: 'reporting.border.left_border', defaultMessage: 'Left border' }),
          value: STROKE_BORDER_POSITIONS.LEFT
        },
        {
          name: intl.formatMessage({
            id: 'reporting.border.top_and_bottom_border',
            defaultMessage: 'Top and bottom border'
          }),
          value: STROKE_BORDER_POSITIONS.TOP_BOTTOM
        },
        {
          name: intl.formatMessage({
            id: 'reporting.border.all_around_border',
            defaultMessage: 'All round border'
          }),
          value: STROKE_BORDER_POSITIONS.ALL_ROUND
        }
      ],
      scalingOptions: [
        {
          name: intl.formatMessage({ id: 'reporting.scaling.auto', defaultMessage: 'Auto scaling' }),
          value: SCALING_OPTIONS.AUTO
        },
        {
          name: intl.formatMessage({ id: 'reporting.scaling.min_max', defaultMessage: 'Min max' }),
          value: SCALING_OPTIONS.MIN_MAX
        }
      ],
      roundingOptions: [
        { name: '0.00', value: ROUNDING_OPTIONS.TWO_DECIMALS },
        { name: '0.0', value: ROUNDING_OPTIONS.ONE_DECIMAL },
        { name: '0', value: ROUNDING_OPTIONS.INTEGER },
        // { name: 'Tens', value: ROUNDING_OPTIONS.TENS },
        // { name: 'Hundreds', value: ROUNDING_OPTIONS.HUNDREDS },
        {
          name: intl.formatMessage({ id: 'reporting.rounding.thousands', defaultMessage: 'Thousands' }),
          value: ROUNDING_OPTIONS.THOUSANDS
        },
        {
          name: intl.formatMessage({ id: 'reporting.rounding.millions', defaultMessage: 'Millions' }),
          value: ROUNDING_OPTIONS.MILLIONS
        }
      ],
      groupingOptions,
      driversGroupingOptions,
      allGroupingOptions: [...groupingOptions, ...driversGroupingOptions],
      valueTreatmentOptions: [
        {
          name: intl.formatMessage({ id: 'formula.sum', defaultMessage: 'SUM' }),
          value: VALUE_TREATMENT.SUM
        },
        {
          name: intl.formatMessage({ id: 'reporting.treatment.average', defaultMessage: 'Average' }),
          value: VALUE_TREATMENT.AVERAGE
        }
      ],
      unitOptions: [
        {
          name: intl.formatMessage({ id: 'common.default', defaultMessage: 'Default' }),
          value: ''
        },
        {
          name: intl.formatMessage({ id: 'common.number', defaultMessage: 'Number' }),
          value: VALUE_DISPLAY_OPTIONS.NUMBER
        },
        {
          name: intl.formatMessage({ id: 'common.currency', defaultMessage: 'Currency' }),
          value: VALUE_DISPLAY_OPTIONS.CURRENCY
        },
        {
          name: intl.formatMessage({ id: 'common.percentage', defaultMessage: 'Percentage' }),
          value: VALUE_DISPLAY_OPTIONS.PERCENTAGE_RAW
        }
      ],
      periodEndOptions: [
        {
          name: intl.formatMessage({ id: 'reporting.period.custom_dates', defaultMessage: 'Custom dates' }),
          value: PERIOD_END_OPTIONS.CUSTOM_DATES,
          type: 'daily',
          typeName: 'Days',
          start: PERIOD_OPTIONS.OTHER
        },
        {
          name: intl.formatMessage({ id: 'reporting.period.mixed_dates', defaultMessage: 'Mixed dates' }),
          value: PERIOD_END_OPTIONS.MIXED,
          type: 'daily',
          typeName: 'Days',
          start: PERIOD_OPTIONS.OTHER
        },
        {
          name: intl.formatMessage({ id: 'common.yesterday', defaultMessage: 'Yesterday' }),
          value: PERIOD_END_OPTIONS.YESTERDAY,
          type: 'daily',
          typeName: 'Days',
          start: PERIOD_OPTIONS.LAST_PERIOD
        },
        {
          name: intl.formatMessage({ id: 'common.today', defaultMessage: 'Today' }),
          value: PERIOD_END_OPTIONS.TODAY,
          type: 'daily',
          typeName: 'Days',
          start: PERIOD_OPTIONS.THIS_PERIOD
        },
        {
          name: intl.formatMessage({
            id: 'reporting.period.custom_month_end',
            defaultMessage: 'Custom month end'
          }),
          value: PERIOD_END_OPTIONS.CUSTOM,
          type: 'monthly',
          typeName: 'Months',
          start: PERIOD_OPTIONS.OTHER
        },
        {
          name: intl.formatMessage({
            id: 'reporting.period.last_week_mon_sun',
            defaultMessage: 'Last week (Mon-Sun)'
          }),
          value: PERIOD_END_OPTIONS.LAST_WEEK_MON,
          type: 'weekly_mon',
          typeName: 'Weeks (Mon-Sun)',
          start: PERIOD_OPTIONS.LAST_PERIOD
        },
        {
          name: intl.formatMessage({
            id: 'reporting.period.this_week_mon_sun',
            defaultMessage: 'This week (Mon-Sun)'
          }),
          value: PERIOD_END_OPTIONS.THIS_WEEK_MON,
          type: 'weekly_mon',
          typeName: 'Weeks (Mon-Sun)',
          start: PERIOD_OPTIONS.THIS_PERIOD
        },
        {
          name: intl.formatMessage({
            id: 'reporting.period.last_week_sun_sat',
            defaultMessage: 'Last week (Sun-Sat)'
          }),
          value: PERIOD_END_OPTIONS.LAST_WEEK_SUN,
          type: 'weekly_sun',
          typeName: 'Weeks (Sun-Sat)',
          start: PERIOD_OPTIONS.LAST_PERIOD
        },
        {
          name: intl.formatMessage({
            id: 'reporting.period.this_week_sun_sat',
            defaultMessage: 'This week (Sun-Sat)'
          }),
          value: PERIOD_END_OPTIONS.THIS_WEEK_SUN,
          type: 'weekly_sun',
          typeName: 'Weeks (Sun-Sat)',
          start: PERIOD_OPTIONS.THIS_PERIOD
        },
        {
          name: intl.formatMessage({
            id: 'reporting.period.last_calendar_month',
            defaultMessage: 'Last calendar month'
          }),
          value: PERIOD_END_OPTIONS.LAST_MONTH,
          type: 'monthly',
          typeName: 'Months',
          start: PERIOD_OPTIONS.LAST_PERIOD
        },
        {
          name: intl.formatMessage({
            id: 'reporting.period.this_calendar_month',
            defaultMessage: 'This calendar month'
          }),
          value: PERIOD_END_OPTIONS.THIS_MONTH,
          type: 'monthly',
          typeName: 'Months',
          start: PERIOD_OPTIONS.THIS_PERIOD
        },
        {
          name: intl.formatMessage({
            id: 'reporting.period.last_fin_quarter',
            defaultMessage: 'Last financial quarter'
          }),
          value: PERIOD_END_OPTIONS.LAST_QUARTER,
          type: 'quarterly',
          typeName: 'Quarters',
          start: PERIOD_OPTIONS.LAST_PERIOD
        },
        {
          name: intl.formatMessage({
            id: 'reporting.period.this_fin_quarter',
            defaultMessage: 'This financial quarter'
          }),
          value: PERIOD_END_OPTIONS.THIS_QUARTER,
          type: 'quarterly',
          typeName: 'Quarters',
          start: PERIOD_OPTIONS.THIS_PERIOD
        },
        {
          name: intl.formatMessage({
            id: 'reporting.period.last_fin_year',
            defaultMessage: 'Last financial year'
          }),
          value: PERIOD_END_OPTIONS.LAST_FIN_YEAR,
          type: 'yearly',
          typeName: 'Years',
          start: PERIOD_OPTIONS.LAST_PERIOD
        },
        {
          name: intl.formatMessage({
            id: 'reporting.period.this_fin_year',
            defaultMessage: 'This financial year'
          }),
          value: PERIOD_END_OPTIONS.THIS_FIN_YEAR,
          type: 'yearly',
          typeName: 'Years',
          start: PERIOD_OPTIONS.THIS_PERIOD
        }
      ] as PeriodEndOption[],
      orientations: {
        portrait: intl.formatMessage({ id: 'reporting.portrait', defaultMessage: 'Portrait' }),
        landscape: intl.formatMessage({ id: 'reporting.landscape', defaultMessage: 'Landscape' }),
        dashboard: intl.formatMessage({ id: 'reporting.dashboard', defaultMessage: 'Dashboard' })
      },
      getTransformedColumnFormulas,
      getFinDataQueryForWidget,
      getComparisonQueryForWidget,
      hasTemplateDS,
      getDummyTemplateDSInReport,
      getDataReferencesFromReport: getDataReferencesFromReportCallback,
      mapTemplateDatasets,
      mapWorkspaceTemplateDatasets,
      monthsOfTheYear: [
        intl.formatMessage({ id: 'common.january', defaultMessage: 'January' }),
        intl.formatMessage({ id: 'common.february', defaultMessage: 'February' }),
        intl.formatMessage({ id: 'common.march', defaultMessage: 'March' }),
        intl.formatMessage({ id: 'common.april', defaultMessage: 'April' }),
        intl.formatMessage({ id: 'common.may', defaultMessage: 'May' }),
        intl.formatMessage({ id: 'common.june', defaultMessage: 'June' }),
        intl.formatMessage({ id: 'common.july', defaultMessage: 'July' }),
        intl.formatMessage({ id: 'common.august', defaultMessage: 'August' }),
        intl.formatMessage({ id: 'common.september', defaultMessage: 'September' }),
        intl.formatMessage({ id: 'common.october', defaultMessage: 'October' }),
        intl.formatMessage({ id: 'common.november', defaultMessage: 'November' }),
        intl.formatMessage({ id: 'common.december', defaultMessage: 'December' })
      ],
      getDatePeriodNameKey: (period: 'MONTHLY' | 'WEEKLY_MONDAY' | 'WEEKLY_SUNDAY') =>
        period === 'MONTHLY'
          ? intl.formatMessage({ id: 'common.month', defaultMessage: 'month' })
          : intl.formatMessage({ id: 'common.week', defaultMessage: 'week' }),
      zoomOptions: [
        { label: intl.formatMessage({ id: 'reporting.actual_size', defaultMessage: 'Actual size' }), value: 1 },
        { label: intl.formatMessage({ id: 'reporting.fit_canvas', defaultMessage: 'Fit canvas' }), value: 0 },
        { label: '50%', value: 0.5 },
        { label: '75%', value: 0.75 },
        { label: '90%', value: 0.9 },
        { label: '100%', value: 1 },
        { label: '125%', value: 1.25 },
        { label: '150%', value: 1.5 },
        { label: '200%', value: 2 }
      ],
      getPageNumberLabel,
      getHighestUntitledReportNum,
      getHighestUntitledTemplateNum,
      getDateFieldTitleDesc,
      roundAndFormatValue,
      marginOptions: {
        Top: intl.formatMessage({ id: 'reporting.margin.top', defaultMessage: 'Top' }),
        Bottom: intl.formatMessage({ id: 'reporting.margin.bottom', defaultMessage: 'Bottom' }),
        Left: intl.formatMessage({ id: 'reporting.margin.left', defaultMessage: 'Left' }),
        Right: intl.formatMessage({ id: 'reporting.margin.right', defaultMessage: 'Right' })
      }
    }),
    [
      intl,
      liveForecastName,
      dummyTemplateForecasts,
      datasetComparisonChildren,
      actualForecasts,
      groupingOptions,
      driversGroupingOptions,
      getTransformedColumnFormulas,
      getFinDataQueryForWidget,
      getComparisonQueryForWidget,
      hasTemplateDS,
      getDummyTemplateDSInReport,
      getDataReferencesFromReportCallback,
      mapTemplateDatasets,
      mapWorkspaceTemplateDatasets,
      getPageNumberLabel,
      getHighestUntitledReportNum,
      getHighestUntitledTemplateNum,
      getDateFieldTitleDesc,
      roundAndFormatValue
    ]
  );

  return constants;
};
